import {Button, ButtonProps} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {CheckboxContext} from "../../../project/shopdrawing/Checkbox";
import {api} from "../../../../api/API";
import {purchaseOrderItemsTableName} from "../Items";
import {toSourceTableName} from "../PartsToSource";
import {TableSelectionContext, useTableData} from "../../../../misc/scroller/Table";
import {SnackContext} from "../../../../misc/Snackbar";
import {PurchaseOrderItem, ToSourceItem} from "../../../../api/Logistics";
import {groupByArr, sum} from "nate-react-api-helpers";
import {purchasingPopoutPanel} from "../PurchaseOrderList";
import {decorate} from "../../../../misc/pdf/parts/CoverPage";
import {humanizeDoorCore} from "../../../project/quote/pricing/QuotePDF";
import {findValues} from "../FindValues";
import {ToDeliverItem} from "../../../../api/Deliveries";
import {fixFinishName, Preps} from "../../../../api/Products";
import {useDropdownDataIMP, ValueItem} from "../../../project/settings/DropDowns";
import {woodDoor} from "../../../project/quote/openings/Const";

export function SubmitButton(props: {
    purchaseOrder?: number;
    isDraft: boolean;
    configureSourceGroup?: number;
    size?: ButtonProps["size"];
    sourceMode: "stock" | "to-order";
}) {
    const {listenAll} = useContext(CheckboxContext);
    const [selected, setSelected] = useState<number[]>([]);

    const ctx = useContext(TableSelectionContext);
    const snack = useContext(SnackContext);

    useEffect(() => {
        const sub = listenAll(setSelected)
        return () => sub.cancel();
    }, [listenAll])

    const data = useTableData<ToSourceItem>(toSourceTableName)
    const isRestock = props.sourceMode === "stock";

    const ddlRef = useDropdownDataIMP();

    return (
        <div style={{padding: 4,}}>
            {props.configureSourceGroup ?
                <Button onClick={async () => {
                    if(!props.configureSourceGroup) return;

                    try {
                        snack.loading();

                        await api.configureSourcing.addItems({
                            group: props.configureSourceGroup,
                            inventoryRequests: selected
                        })

                        ctx.refresh(toSourceTableName);
                        snack.success("Added")
                    } catch(e: any) {
                        snack.error(e.toString())
                        ctx.refresh(toSourceTableName);
                    }
                }} variant="contained" size={props.size} disabled={!props.configureSourceGroup || !props.isDraft || selected.length === 0}>
                    Add to Group
                </Button> :
                    <Button onClick={async () => {
                        if(!props.purchaseOrder) return;
                        const purchaseOrder = props.purchaseOrder;

                        try {
                            snack.loading();

                            const ddlOptions = await ddlRef.current();

                            const toAdd = data.filter(d => selected.indexOf(d.id) !== -1);
                            const updates = groupByArr(toAdd, item => item.product + "." + formatDescription(item, {ddlOptions}) + "." + item.openingHardwareGroup + "." + item.linkedMiscPrice + "." + item.linkedProjectWorkOrderItem)

                            let list: Omit<PurchaseOrderItem, "logisticsQtyOutstanding">[] = [];

                            for(const grp of updates) {
                                list.push({
                                    id: 0,
                                    purchaseOrder: purchaseOrder,
                                    reference: "",
                                    description: (grp.values[0].linkedMiscPrice || grp.values[0].linkedProjectWorkOrderItem) ? grp.values[0].name : formatDescription(grp.values[0], {ddlOptions}),
                                    code: grp.values[0].productCode,
                                    linkedMiscPrice: grp.values[0].linkedMiscPrice,
                                    linkedProjectWorkOrderItem: grp.values[0].linkedProjectWorkOrderItem,
                                    qty: sum(grp.values.map(v => v.qty)),
                                    unitPriceCents: grp.values[0].lastPrice || grp.values[0].lastPriceAtOthers || 0,
                                    inventoryRequests: grp.values.map(v => {
                                        const list: number[] = [];

                                        if (v.inventoryRequestId) {
                                            list.push(v.inventoryRequestId)
                                        }

                                        if (v.frameAnchorDetail) {
                                            list.push(...findValues(v.frameAnchorDetail, v => v.inventoryRequestId));
                                        }

                                        return list;
                                    }).flat(),
                                    archived: false,
                                    qtyPendingReceiving: 0,
                                    qtyBackOrdered: 0,
                                    updatedAt: (new Date()) as any,
                                    kind: grp.values[0].kind as any,
                                    qtyOutstanding: 0,
                                    createStockingInventoryRequestForProduct: isRestock ? grp.values[0].product : undefined,
                                    doorPreps: {},
                                    framePreps: {}
                                })
                            }

                            await api.logistics.updatePurchaseOrderItemBulk(list);
                            purchasingPopoutPanel.sendToServer("refresh-po");
                            ctx.refresh(toSourceTableName);
                            snack.success("Added")
                        } catch(e: any) {
                            snack.error(e.toString())
                            ctx.refresh(purchaseOrderItemsTableName);
                            ctx.refresh(toSourceTableName);
                        }
                    }} variant="contained" disabled={!props.purchaseOrder || !props.isDraft || selected.length === 0}>Add to PO</Button>
            }
        </div>
    );
}

export type DescriptionInput = {
    categoryName: string|null;
    doorCore: string|null;
    doorMaterial: string|null;
    doorSeries: string|null;
    doorGauge: string|null;
    doorThickness: string|null;
    doorSeam: string|null;
    doorElevation: string|null;
    doorLabel: string|null;
    doorFinish: string|null;
    doorPreps: Preps | null;

    frameProfile: string |null;
    frameSeries: string |null;
    frameConstruction: string |null;
    frameType: string |null;
    frameMaterial: string |null;
    frameHanding: string |null;
    frameLabel: string |null;
    frameGauge: string|null;
    frameScreenElev: string|null;
    frameJambDepth: string|null;
    frameDoorThickness: string|null;
    framePreps: Preps | null;

    div10Category: string |null;
    div10LockerTiers: string |null;
    div10LockerCoating: string |null;
    div10LockerSloped: boolean |null;
    div10LockerEndPanels: boolean |null;
    div10MailboxLoading: string |null;
    div10MailboxMounting: string | null;
    div10MailboxTenantUnits: number |null;
    div10MailboxParcelUnits: number |null;
    div10PartitionBracing: string |null;
    div10PartitionCoating: string |null;
    div10Description: string |null;
    div10Note: string |null;

    frameAnchorName?: string | null;

    description: string;
    productCode: string;
    finish: string;
    dimensions: string;
    keyingDetail?: string | null;

    dimWidth: string;
    dimHeight: string;
    dimLength: string;
}

function getCutDown(p: Preps | null | undefined) {
    if(!p) return;
    const cd = p.cutDown;
    if(!cd || cd.type !== "cutDown") return;

    if(cd.cutDown.overCut && cd.cutDown.underCut) {
        return `(${cd.cutDown.overCut} Over Cut, ${cd.cutDown.underCut} Under Cut)`
    }

    if(cd.cutDown.overCut) {
        return `(${cd.cutDown.overCut} Over Cut)`
    }

    if(cd.cutDown.underCut) {
        return `(${cd.cutDown.underCut} Under Cut)`
    }

    return undefined;
}

export function formatDescription(input: DescriptionInput | ToDeliverItem, opts: {
    ddlOptions: ValueItem[]
    plural?: boolean;
    forQuote?: boolean;
}) {
    const plural = opts.plural || false;
    const forQuote = opts.forQuote || false;

    if(!!input.doorSeries) {
        const series = !input.doorSeries ? null : plural ? input.doorSeries.replace("Door", "Doors") : input.doorSeries;
        const core = humanizeDoorCore(opts.ddlOptions, input.doorCore, input.doorSeries);
        const cutDown = getCutDown(input.doorPreps)

        let list = [
            input.doorMaterial,
            core,
            series,
            "-",
            input.doorGauge ? input.doorGauge + "ga" : "",
            "-",
            input.dimWidth + " x " + input.dimHeight + " x " + input.doorThickness,
            "-",
            input.doorSeam,
            "-",
            input.doorElevation,
            "-",
            input.doorLabel,
            cutDown
        ]

        if(series?.indexOf("HM Door") !== -1) {
            list = [
                series,
                "-",
                input.doorGauge ? input.doorGauge + "ga" : "",
                core,
                "-",
                input.dimWidth + " x " + input.dimHeight + " x " + input.doorThickness,
                "-",
                input.doorSeam,
                "-",
                input.doorElevation,
                "-",
                input.doorLabel,
                cutDown
            ]
        }

        if(input.doorSeries === woodDoor && input.doorFinish) {
            const finishLabel = opts.ddlOptions.find(d => d.category === "doorMaterialWoodFinish" && d.value === input.doorFinish)?.calculatedPrintLabel || input.doorFinish;
            list.push(finishLabel);
        }

        return list
            .filter(v => !!v)
            .join(" ")
            .replace(/( -){2,}/g, " -")
            .replace(/[- ]+$/g, "")
    }

    if(input.frameSeries !== null && input.frameSeries !== undefined) {

        const cutDown = getCutDown(input.framePreps)
        let series = !input.frameSeries ? null : plural ? input.frameSeries.replace("Frame", "Frames") : input.frameSeries;
        const isHeader = input.categoryName === "Header";
        const isJamb = input.categoryName === "Hinge Jamb" || input.categoryName === "Strike Jamb";
        const isWindow = input.frameType === "Window";
        let weirdFrameProfile = input.frameProfile && input.frameProfile !== "STD" ? input.frameProfile : null;
        if(weirdFrameProfile && series) {
            const opt = opts.ddlOptions.find(d => d.category === "frameProfile" && d.value === input.frameProfile);
            if(opt) {
                weirdFrameProfile = opt.calculatedPrintLabel;
            }

            // use replace b/c of pluralizing above
            series = series.replace("HM Frame", `HM ${weirdFrameProfile} Frame`)
        }

        // skip blank parts (e.g. expandable frames have no jamb depth)
        const dims = (...parts: (string | null | undefined)[]) => parts.filter(v => !!v).join(" x ");
        const doorThickness = input.frameDoorThickness ? `${input.frameDoorThickness}DT` : null;

        return [
            input.frameType,
            series,
            (isHeader || isJamb) ? input.categoryName : "",
            "-",
            decorate(input.frameGauge, "", "ga"),
            input.frameConstruction,
            "-",
            forQuote ?
                isHeader ? dims(input.dimWidth, input.frameJambDepth) :
                    isJamb ? dims(input.dimHeight, input.frameJambDepth) :
                        dims(input.dimWidth, input.dimHeight, input.frameJambDepth) :
            isHeader ? dims(input.dimWidth, input.frameJambDepth, doorThickness) :
                isJamb ? dims(input.dimHeight, input.frameJambDepth, doorThickness) :
                    isWindow ? dims(input.dimWidth, input.dimHeight, input.frameJambDepth) :
                        dims(input.dimWidth, input.dimHeight, input.frameJambDepth, doorThickness),
            "-",
            input.frameLabel,
            "-",
            input.frameScreenElev,
            "-",
            input.frameAnchorName,
            cutDown,
        ]
            .filter(v => !!v)
            .join(" ")
            .replace(/( -){2,}/g, " -")
            .replace(/[- ]+$/g, "")
    }


    if(!!input.div10Category) {
        switch(input.div10Category) {
            case 'corner-guard':
                return [
                    "Corner Guard",
                    input.productCode,
                    input.dimWidth + "W x " + input.dimHeight + "H",
                ].filter(v => !!v).join(" ")
            case "locker":
                return [
                    "Locker",
                    input.div10LockerTiers,
                    input.div10LockerCoating,
                    input.div10LockerSloped ? "sloped" : "",
                    input.div10LockerEndPanels ? "with end panels" : "",
                    input.dimWidth + "W x " + input.dimHeight + "H x " + input.dimLength + "D",
                    input.div10Note,
                ].filter(v => !!v).join(" ")
            case "mailbox":
                return [
                    "Mailbox",
                    input.div10MailboxLoading,
                    input.div10MailboxMounting,
                    input.div10MailboxTenantUnits + " Tenant Units",
                    input.div10MailboxParcelUnits + " Parcel Units",
                    input.div10Note,
                ].filter(v => !!v).join(" ")
            case "wr-accessory":
                // use hardware format
                break;
            case "wr-partition":
                return [
                    "Partition",
                    input.div10PartitionBracing,
                    input.div10PartitionCoating,
                    input.div10Description,
                    input.div10Note,
                ].filter(v => !!v).join(" ")
            default:
                console.warn("unknown div10 category")
                break;
        }
    }

    return [
        input.description,
        "-",
        input.productCode,
        fixFinishName(input.finish),
        decorate(input.keyingDetail, "Keying detail: ", "") || "",
        input.dimensions
    ].filter(v => !!v).join(" ")
}